import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Logo } from '@ui/components/Logo';
import { Icon } from '@ui/components/Icon';
import { api, clearToken, token } from './lib/api';
import { Login } from './pages/Login';
import { Overview } from './pages/Overview';
import { Tenants } from './pages/Tenants';
import { TenantDetail } from './pages/TenantDetail';
import { Signups } from './pages/Signups';
import { Invoices } from './pages/Invoices';
import { ManualPayments } from './pages/ManualPayments';
import { Plans } from './pages/Plans';

interface Me {
  id: string;
  role: 'SUPERADMIN' | 'BILLING' | 'SUPPORT';
}

const ROLE_LABELS: Record<Me['role'], string> = {
  SUPERADMIN: 'مسؤول أعلى',
  BILLING: 'الفوترة',
  SUPPORT: 'الدعم',
};

export function App() {
  if (!token()) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route
        path="*"
        element={
          <Shell>
            <Routes>
              <Route path="/" element={<Overview />} />
              <Route path="/tenants" element={<Tenants />} />
              <Route path="/tenants/:id" element={<TenantDetail />} />
              <Route path="/signups" element={<Signups />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/manual-payments" element={<ManualPayments />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Shell>
        }
      />
    </Routes>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api<Me>('/me'),
    retry: false,
  });

  /* عدّادُ الحوالاتِ المعلّقةِ لشارةِ التنبيهِ في القائمة. */
  const summary = useQuery({
    queryKey: ['summary'],
    queryFn: () => api<{ pendingTransfers?: number }>('/summary'),
    retry: false,
  });
  const pendingTransfers = summary.data?.pendingTransfers ?? 0;

  const logout = () => {
    clearToken();
    navigate('/login', { replace: true });
  };

  return (
    <div className="console-shell">
      <header className="console-bar">
        <div className="console-brand">
          <Logo size={26} />
          <span>لوحة المنصة</span>
        </div>

        <nav className="console-nav">
          <Item to="/" label="نظرة عامة" />
          <Item to="/tenants" label="المستأجرون" />
          <Item to="/signups" label="طلبات التسجيل" />
          <Item to="/invoices" label="الفواتير" />
          <Item to="/manual-payments" label="الحوالات" badge={pendingTransfers} />
          <Item to="/plans" label="الباقات" />
        </nav>

        <div className="console-user">
          {me.data && <span>{ROLE_LABELS[me.data.role]}</span>}
          <button className="btn btn-sm" onClick={logout}>
            <Icon name="logout" size={14} />
            خروج
          </button>
        </div>
      </header>

      <main className="console-body">{children}</main>
    </div>
  );
}

function Item({ to, label, badge }: { to: string; label: string; badge?: number }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) => `console-link${isActive ? ' is-active' : ''}`}
    >
      {label}
      {badge ? <span className="console-badge">{badge}</span> : null}
    </NavLink>
  );
}
